import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { cn } from '@/lib/utils';

type FieldHeaderProps = {
  title: string;
  description?: string;
  error?: string;
  action?: React.ReactNode;
  /** Shows an info icon after the title that reveals this text on hover or focus. */
  tooltip?: string;
};

export const FieldHeader = ({ title, description, error, action, tooltip }: FieldHeaderProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-start gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-sm leading-5">
        <span
          className={cn(
            'flex items-center gap-1 font-medium text-foreground',
            error && 'text-warning'
          )}
        >
          {title}
          {tooltip && (
            <InfoTooltip content={tooltip} size="large" side="top">
              <button
                type="button"
                aria-label={t('common.information')}
                className="relative rounded-sm text-foreground-muted after:absolute after:-inset-2 after:content-[''] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                <InfoIcon size={16} />
              </button>
            </InfoTooltip>
          )}
        </span>
        {description && <span className="text-foreground-muted">{description}</span>}
        {error && (
          <span role="alert" className="text-warning">
            {error}
          </span>
        )}
      </div>
      {action}
    </div>
  );
};
